import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchLogLevelAuditAPI,
  fetchRuntimeLogLevelsAPI,
  updateRuntimeLogLevelAPI,
} from '../../api/runtimeLogLevel';
import { notify, notifyError } from '../../utils/notify';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

/** Winston-style levels sent to API: error, warn, info, debug */
const LEVEL_OPTIONS = [
  { value: 'info', label: 'info' },
  { value: 'error', label: 'error' },
  { value: 'warn', label: 'warn' },
  { value: 'debug', label: 'debug' },
];

const formatLevelLabel = (v) => {
  if (v == null || v === '') {
    return '—';
  }
  return v === 'warning' ? 'warn' : v;
};

const TARGETS = [
  {
    key: 'api',
    title: 'Main API',
    description: 'Sails app and main RabbitMQ workers',
  },
  {
    key: 'jobs_microservice',
    title: 'Jobs microservice',
    description: 'Jobs microservice Sails app and workers',
  },
  {
    key: 'node_jobs',
    title: 'Node jobs',
    description: 'Scripts under node_jobs/',
  },
];

const ChangeLogLevelTab = ({ summary, summaryLoading }) => {
  const [serviceKey, setServiceKey] = useState(undefined);
  const [level, setLevel] = useState('info');
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();

  useEffect(() => {
    setReason('');
  }, [serviceKey]);

  const target = TARGETS.find((t) => t.key === serviceKey);
  const info = serviceKey ? summary[serviceKey] : null;

  const mutation = useMutation((body) => updateRuntimeLogLevelAPI(body), {
    onSuccess: () => {
      notify('Log level updated', 'success');
      setReason('');
      queryClient.invalidateQueries(['runtimeLogLevel']);
      queryClient.invalidateQueries(['logLevelAudit']);
    },
    onError: (e) => {
      notifyError(e, false, 'Failed to update log level');
    },
  });

  const apply = () => {
    if (!serviceKey) {
      notify('Select a service first', 'warning');
      return;
    }
    if (!reason.trim()) {
      notify('Reason is required', 'warning');
      return;
    }
    mutation.mutate({
      target: serviceKey,
      level,
      reason: reason.trim(),
    });
  };

  const clearOverride = () => {
    if (!serviceKey) {
      notify('Select a service first', 'warning');
      return;
    }
    if (!reason.trim()) {
      notify('Reason is required', 'warning');
      return;
    }
    mutation.mutate({
      target: serviceKey,
      level: '',
      reason: reason.trim(),
    });
  };

  if (summaryLoading) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Alert
        type="warning"
        showIcon
        message="Overrides expire after 1 hour"
        description={
          <span>
            After you apply a level, it takes effect across processes within
            about 90 seconds. Each override lasts <strong>1 hour</strong> and
            then the key expires in Redis; logging reverts to the server{' '}
            <strong>LOG_LEVEL</strong> environment value (default info when
            unset). Use a clear reason; all updates are audited.
          </span>
        }
        style={{ marginBottom: 16 }}
      />
      <div style={{ marginBottom: 16 }}>
        <label htmlFor="runtime-log-service">
          <Text strong>Service</Text>
        </label>
        <Select
          id="runtime-log-service"
          aria-label="Service to change log level for"
          placeholder="Select a service"
          value={serviceKey}
          onChange={setServiceKey}
          style={{
            width: '100%',
            maxWidth: 400,
            display: 'block',
            marginTop: 8,
          }}
          allowClear
        >
          {TARGETS.map((t) => (
            <Option key={t.key} value={t.key}>
              {t.title}
            </Option>
          ))}
        </Select>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Choose which deployment receives the runtime log level override.
        </Text>
      </div>
      {target ? (
        <Card title={target.title}>
          <Text type="secondary">{target.description}</Text>
          <div style={{ marginTop: 12 }}>
            <div>
              <Text strong>Redis override: </Text>
              <Text>
                {info?.redisLevel != null
                  ? formatLevelLabel(info.redisLevel)
                  : '(none — using LOG_LEVEL)'}
              </Text>
            </div>
            <div>
              <Text strong>Effective level: </Text>
              <Text>{formatLevelLabel(info?.effectiveLevel)}</Text>
            </div>
            <div>
              <Text strong>Env default (LOG_LEVEL): </Text>
              <Text>{formatLevelLabel(info?.envDefault)}</Text>
            </div>
            {serviceKey === 'api' && summary?.apiProcessLevel ? (
              <div>
                <Text strong>This API process (Winston): </Text>
                <Text>{formatLevelLabel(summary.apiProcessLevel)}</Text>
              </div>
            ) : null}
          </div>
          <Row gutter={8} style={{ marginTop: 16 }}>
            <Col span={6}>
              <Select
                aria-label="Log level"
                value={level}
                onChange={setLevel}
                style={{ width: '100%' }}
              >
                {LEVEL_OPTIONS.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={18}>
              <TextArea
                aria-label="Reason for change"
                rows={2}
                placeholder="Reason (required for audit)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Col>
          </Row>
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={apply}
              loading={mutation.isLoading}
              style={{ marginRight: 8 }}
            >
              Apply to Redis
            </Button>
            <Button onClick={clearOverride} loading={mutation.isLoading}>
              Clear override
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
};

const AuditHistoryTab = () => {
  const auditQuery = useQuery(['logLevelAudit'], async () => {
    const { data } = await fetchLogLevelAuditAPI({ limit: 50 });
    return Array.isArray(data) ? data : [];
  });

  const auditColumns = [
    { title: 'When', dataIndex: 'createdAt', key: 'createdAt', width: 200 },
    { title: 'User id', dataIndex: 'user', key: 'user', width: 90 },
    {
      title: 'Email',
      dataIndex: 'userEmail',
      key: 'userEmail',
      width: 200,
      ellipsis: true,
      render: (v) => (v == null || v === '' ? '—' : v),
    },
    {
      title: 'Name',
      dataIndex: 'userName',
      key: 'userName',
      width: 160,
      ellipsis: true,
      render: (v) => (v == null || v === '' ? '—' : v),
    },
    { title: 'Target', dataIndex: 'target', key: 'target', width: 160 },
    {
      title: 'Previous',
      dataIndex: 'previousLevel',
      key: 'previousLevel',
      width: 100,
      render: (v) => formatLevelLabel(v),
    },
    {
      title: 'New',
      dataIndex: 'newLevel',
      key: 'newLevel',
      width: 100,
      render: (v) => formatLevelLabel(v),
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason' },
  ];

  if (auditQuery.isLoading) {
    return <Spin size="large" />;
  }

  return (
    <Table
      rowKey="id"
      dataSource={auditQuery.data || []}
      columns={auditColumns}
      pagination={false}
      scroll={{ x: true }}
    />
  );
};

const RuntimeLogLevelPage = () => {
  const summaryQuery = useQuery(['runtimeLogLevel'], async () => {
    const { data } = await fetchRuntimeLogLevelsAPI();
    return data;
  });

  const summary = summaryQuery.data || {};

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>Runtime log level</Typography.Title>
      <Tabs defaultActiveKey="change">
        <TabPane tab="Change log level" key="change">
          <ChangeLogLevelTab
            summary={summary}
            summaryLoading={summaryQuery.isLoading}
          />
        </TabPane>
        <TabPane tab="Audit reasons" key="audit">
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Log level changes and the reasons entered for each update.
          </Typography.Paragraph>
          <AuditHistoryTab />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default RuntimeLogLevelPage;
